import "./FileSummary.css";
import {
  capList,
  formatFileTypeLabel,
  formatLanguageLabel,
  formatRiskLabel,
  formatRouteRef,
  formatStringLike,
  formatSymbolRef,
  getRiskSectionTitle,
  hasEngineeringMap,
  isElevatedRisk,
  normalizeDisplayList,
  resolvePurpose,
} from "./fileEngineeringMapDisplay";

function SummaryBadge({ label, className = "" }) {
  if (!label) return null;
  return <span className={`fs-badge ${className}`.trim()}>{label}</span>;
}

function SummarySection({ title, children }) {
  if (!children) return null;
  return (
    <section className="file-summary-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function SurfaceValueBlock({ label, valuesText, remaining = 0 }) {
  if (!valuesText) return null;
  return (
    <div className="file-summary-surface-group">
      <div className="file-summary-surface-label">{label}</div>
      <div className="file-summary-surface-values">
        {valuesText}
        {remaining > 0 && (
          <span className="file-summary-more"> (+{remaining} more)</span>
        )}
      </div>
    </div>
  );
}

function CappedBulletList({ items, max = 5 }) {
  const normalized = normalizeDisplayList(items, formatStringLike, { preserveOrder: true });
  if (normalized.length === 0) return null;
  const { visible, remaining } = capList(normalized, max);
  return (
    <ul className="file-summary-bullet-list">
      {visible.map((item) => (
        <li key={item}>{item}</li>
      ))}
      {remaining > 0 && (
        <li className="file-summary-more">+{remaining} more</li>
      )}
    </ul>
  );
}

function RiskBlock({ engineeringMap }) {
  const riskLevel = String(engineeringMap?.riskLevel || "unknown").toLowerCase();
  const riskReasons = normalizeDisplayList(engineeringMap?.riskReasons, formatStringLike, {
    preserveOrder: true,
  });
  const sectionTitle = getRiskSectionTitle(riskLevel);
  const elevated = isElevatedRisk(riskLevel);
  const riskClass = elevated
    ? riskLevel === "high"
      ? "fs-risk-high"
      : "fs-risk-medium"
    : "fs-behavior-notes";

  if (!elevated && riskReasons.length === 0) return null;

  return (
    <section className={`file-summary-risk ${riskClass}`.trim()}>
      <h4>{sectionTitle}</h4>
      {elevated && <p className="file-summary-risk-level">{formatRiskLabel(riskLevel)}</p>}
      {riskReasons.length > 0 && (
        <ul className="file-summary-bullet-list">
          {riskReasons.slice(0, 5).map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
          {riskReasons.length > 5 && (
            <li className="file-summary-more">+{riskReasons.length - 5} more</li>
          )}
        </ul>
      )}
    </section>
  );
}

function EngineeringSurfaces({ engineeringMap }) {
  const exports = engineeringMap?.exports;
  const routes = engineeringMap?.routes;
  const envVars = engineeringMap?.envVars;
  const dataModels = engineeringMap?.dataModels;
  const sideEffects = engineeringMap?.sideEffects;
  const internalImports = engineeringMap?.internalImports;
  const externalPackages = engineeringMap?.externalPackages;

  const groups = [
    { label: "Exports", items: exports, formatter: formatSymbolRef, joinWith: ", " },
    { label: "Routes", items: routes, formatter: formatRouteRef, joinWith: " · " },
    { label: "Environment variables", items: envVars, formatter: formatStringLike, joinWith: ", " },
    { label: "Data models", items: dataModels, formatter: formatStringLike, joinWith: ", " },
    { label: "Side effects", items: sideEffects, formatter: formatStringLike, joinWith: ", " },
    { label: "Internal imports", items: internalImports, formatter: formatStringLike, joinWith: ", " },
    { label: "External packages", items: externalPackages, formatter: formatStringLike, joinWith: ", " },
  ];

  const renderedGroups = groups
    .map((group) => {
      const normalized = normalizeDisplayList(group.items, group.formatter);
      if (normalized.length === 0) return null;
      const { visible, remaining } = capList(normalized, 8);
      return (
        <SurfaceValueBlock
          key={group.label}
          label={group.label}
          valuesText={visible.join(group.joinWith)}
          remaining={remaining}
        />
      );
    })
    .filter(Boolean);

  if (renderedGroups.length === 0) return null;

  return (
    <section className="file-summary-surfaces">
      <h4>Engineering surfaces</h4>
      <div className="file-summary-surface-groups">{renderedGroups}</div>
    </section>
  );
}

function ExtractionQualityNote({ extractionQuality }) {
  if (!extractionQuality || typeof extractionQuality !== "object") return null;
  const { staticOk, llmOk, degraded } = extractionQuality;
  if (degraded) {
    return (
      <p className="file-summary-quality-note">
        Extraction quality: partial (static {staticOk ? "ok" : "limited"}, LLM{" "}
        {llmOk ? "ok" : "limited"})
      </p>
    );
  }
  if (staticOk === false || llmOk === false) {
    return (
      <p className="file-summary-quality-note">
        Extraction quality: static {staticOk ? "ok" : "limited"}, LLM{" "}
        {llmOk ? "ok" : "limited"}
      </p>
    );
  }
  return null;
}

function EngineeringMapSummary({ summary, engineeringMap }) {
  const displayPath = summary.path || engineeringMap.path;
  const purpose = resolvePurpose(summary, engineeringMap);
  const primaryResponsibility = String(engineeringMap.primaryResponsibility || "").trim();
  const fileType = String(engineeringMap.fileType || "unknown").trim();
  const language = engineeringMap.language ? String(engineeringMap.language).trim() : "";
  const riskLevel = String(engineeringMap.riskLevel || "unknown").toLowerCase();
  const whenToRetrieve = engineeringMap.whenToRetrieve;
  const questionsAnswerable = engineeringMap.questionsAnswerable;
  const staticConfidence = engineeringMap.staticConfidence;
  const securityTags = engineeringMap.securityTags;
  const noiseClass = engineeringMap.noiseClass;
  const retrievalHints = engineeringMap.retrievalHints;
  const evidenceReasons = normalizeDisplayList(
    [
      ...(Array.isArray(engineeringMap.staticReasons) ? engineeringMap.staticReasons : []),
      ...(Array.isArray(engineeringMap.evidence)
        ? engineeringMap.evidence.map((item) => item?.reason).filter(Boolean)
        : []),
    ],
    formatStringLike,
    { preserveOrder: true }
  );

  const staticConfidenceLabel =
    typeof staticConfidence === "number" && Number.isFinite(staticConfidence)
      ? staticConfidence >= 0.75
        ? "High static confidence"
        : staticConfidence >= 0.5
          ? "Medium static confidence"
          : "Low static confidence"
      : "";

  return (
    <div className="file-summary">
      <div className="file-summary-header">
        <h3>{displayPath}</h3>
      </div>

      <div className="file-summary-meta">
        <SummaryBadge label={formatFileTypeLabel(fileType)} className="fs-type" />
        {language && (
          <SummaryBadge label={formatLanguageLabel(language)} className="fs-language" />
        )}
        <SummaryBadge
          label={formatRiskLabel(riskLevel)}
          className={`fs-risk-${riskLevel === "high" || riskLevel === "medium" || riskLevel === "low" ? riskLevel : ""}`.trim()}
        />
        {staticConfidenceLabel && (
          <SummaryBadge label={staticConfidenceLabel} className="fs-static-confidence" />
        )}
        {noiseClass && noiseClass !== "none" && (
          <SummaryBadge label={`Noise: ${noiseClass}`} className="fs-noise-class" />
        )}
      </div>

      {normalizeDisplayList(securityTags, formatStringLike).length > 0 && (
        <SummarySection title="Security tags">
          <CappedBulletList items={securityTags} max={6} />
        </SummarySection>
      )}

      {normalizeDisplayList(retrievalHints, formatStringLike, { preserveOrder: true }).length > 0 && (
        <SummarySection title="Retrieval hints">
          <CappedBulletList items={retrievalHints} max={6} />
        </SummarySection>
      )}

      {evidenceReasons.length > 0 && (
        <SummarySection title="Evidence">
          <CappedBulletList items={evidenceReasons} max={6} />
        </SummarySection>
      )}

      {purpose && (
        <SummarySection title="Purpose">
          <p className="file-summary-body-text">{purpose}</p>
        </SummarySection>
      )}

      {primaryResponsibility && (
        <SummarySection title="Primary responsibility">
          <p className="file-summary-body-text">{primaryResponsibility}</p>
        </SummarySection>
      )}

      {normalizeDisplayList(whenToRetrieve, formatStringLike, { preserveOrder: true }).length > 0 && (
        <SummarySection title="When to retrieve">
          <CappedBulletList items={whenToRetrieve} max={5} />
        </SummarySection>
      )}

      {normalizeDisplayList(questionsAnswerable, formatStringLike, { preserveOrder: true }).length > 0 && (
        <SummarySection title="Questions this file can answer">
          <CappedBulletList items={questionsAnswerable} max={5} />
        </SummarySection>
      )}

      <EngineeringSurfaces engineeringMap={engineeringMap} />
      <RiskBlock engineeringMap={engineeringMap} />
      <ExtractionQualityNote extractionQuality={engineeringMap.extractionQuality} />
    </div>
  );
}

function LegacyFileSummary({ summary, displayPath }) {
  const description = String(summary.description || "").trim();
  return (
    <div className="file-summary file-summary-legacy">
      <h3>{displayPath}</h3>
      {description ? (
        <p>{description}</p>
      ) : (
        <p className="file-summary-fallback-note">No summary available for this file.</p>
      )}
      <p className="file-summary-fallback-note">
        Structured engineering map unavailable for this file.
      </p>
    </div>
  );
}

export const FileSummary = ({ summary, path }) => {
  if (!summary) return null;

  const displayPath = summary.path || path;
  if (hasEngineeringMap(summary)) {
    return <EngineeringMapSummary summary={summary} engineeringMap={summary.engineeringMap} />;
  }

  return <LegacyFileSummary summary={summary} displayPath={displayPath} />;
};
